import React, { useState, useEffect, useMemo } from 'react';
import * as XLSX from 'xlsx';
import { formatPrice } from 'common/src/utils/functions';
import { metrics__get_daily_sales } from 'common/src/api/metrics';

interface DailySales {
  date: string;
  revenue: number;
}

const SalesPerDayReport: React.FC = () => {
  const [salesData, setSalesData] = useState<DailySales[]>([]);
  const [startDate, setStartDate] = useState(() => {
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
    return thirtyDaysAgo.toISOString().split('T')[0];
  });
  const [endDate, setEndDate] = useState(
    () => new Date().toISOString().split('T')[0],
  );
  const [tempStartDate, setTempStartDate] = useState(startDate);
  const [tempEndDate, setTempEndDate] = useState(endDate);

  const fetchSalesData = async (start: string, end: string) => 
{
    const { data, error } = await metrics__get_daily_sales(start, end)

    if (error) {
      console.error('Error fetching daily sales data:', error);
      return [];
    }

    return data.map((item) => ({
      date: item.date.split('T')[0], // Ensure date is in YYYY-MM-DD format
      revenue: Number(item.revenue),
    }));
  };

  useEffect(() => {
    fetchSalesData(startDate, endDate).then(setSalesData);
  }, [startDate, endDate]);

  const { revenueData, percentageData, totalRevenue } = useMemo(() => {
    const total = salesData.reduce((sum, day) => sum + day.revenue, 0);
    return {
      revenueData: salesData.reduce(
        (acc, day) => ({ ...acc, [day.date]: day.revenue }),
        {},
      ),
      percentageData: salesData.reduce(
        (acc, day) => ({ ...acc, [day.date]: (day.revenue / total) * 100 }),
        {},
      ),
      totalRevenue: total,
    };
  }, [salesData]);

  const handleDateSubmit = () => {
    setStartDate(tempStartDate);
    setEndDate(tempEndDate);
  };

  const exportToExcel = () => {
    const ws = XLSX.utils.json_to_sheet(salesData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'SalesPerDay');
    XLSX.writeFile(wb, `sales_per_day_${startDate}_${endDate}.xlsx`);
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const dayOfWeek = new Intl.DateTimeFormat('es-ES', {
      weekday: 'short',
    }).format(date);
    const dayOfMonth = date.getDate().toString().padStart(2, '0');
    return `${dayOfWeek} ${dayOfMonth}`;
  };

  return (
    <div className="flex flex-col h-full">
      <div className="flex justify-between p-4 bg-white border-b shadow-sm">
        <div className="w-96">
          <h3 className="text-lg font-semibold mb-3">
            Seleccionar Rango de Fechas
          </h3>
          <div className="space-y-4">
            <div>
              <label
                htmlFor="startDate"
                className="block text-sm font-medium text-gray-700"
              >
                Fecha de Inicio
              </label>
              <input
                id="startDate"
                type="date"
                value={tempStartDate}
                onChange={(e) => setTempStartDate(e.target.value)}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50"
              />
            </div>
            <div>
              <label
                htmlFor="endDate"
                className="block text-sm font-medium text-gray-700"
              >
                Fecha de Fin
              </label>
              <input
                id="endDate"
                type="date"
                value={tempEndDate}
                onChange={(e) => setTempEndDate(e.target.value)}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50"
              />
            </div>
            <button
              onClick={handleDateSubmit}
              className="w-full px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
            >
              Aplicar Rango
            </button>
          </div>
        </div>

        <div className="w-96">
          <h3 className="text-lg font-semibold mb-3">Otras Opciones</h3>
          <button
            onClick={exportToExcel}
            className="w-full px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2"
          >
            Exportar a Excel
          </button>
        </div>
      </div>

      <div className="flex-grow overflow-auto">
        <div className="inline-block min-w-full">
          <div
            className="grid"
            style={{
              gridTemplateColumns: `minmax(100px, auto) repeat(${salesData.length}, minmax(120px, auto)) minmax(150px, auto)`,
              gridTemplateRows: 'auto',
              width: `calc(100px + ${salesData.length * 120}px + 150px + 1px)`, // Add 1px for border
            }}
          >
            {/* Header */}
            <div className="sticky top-0 left-0 z-30 bg-gray-100 font-semibold p-2 border-b border-r border-gray-300">
              Métrica
            </div>
            {salesData.map((day) => (
              <div
                key={day.date}
                className="sticky top-0 z-20 bg-gray-100 font-semibold p-2 text-center border-b border-r border-gray-300 whitespace-nowrap overflow-hidden text-ellipsis"
              >
                {formatDate(day.date)}
              </div>
            ))}
            <div className="sticky top-0 right-0 z-30 bg-gray-100 font-semibold p-2 text-center border-b border-r border-gray-300">
              Total
            </div>

            {/* Revenue row */}
            <div className="sticky left-0 z-10 bg-white font-medium p-2 border-b border-r border-gray-300">
              Revenue
            </div>
            {salesData.map((day) => (
              <div
                key={day.date}
                className="p-2 text-right border-b border-r border-gray-300"
              >
                {formatPrice(revenueData[day.date])}
              </div>
            ))}
            <div className="sticky right-0 z-10 bg-white font-medium p-2 text-right border-b border-r border-gray-300">
              {formatPrice(totalRevenue)}
            </div>

            {/* Percentage row */}
            <div className="sticky left-0 z-10 bg-white font-medium p-2 border-b border-r border-gray-300">
              % Revenue
            </div>
            {salesData.map((day) => (
              <div
                key={day.date}
                className="p-2 text-right border-b border-r border-gray-300"
              >
                {percentageData[day.date].toFixed(2)}%
              </div>
            ))}
            <div className="sticky right-0 z-10 bg-white font-medium p-2 text-right border-b border-r border-gray-300">
              100.00%
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SalesPerDayReport;
